import React from 'react';
import { useBemCN } from '@gik/core/utils/bemBlock';
import { IHistoryEntry } from '@gik/core/models/gik/IHistoryEntry';
import moment from 'moment';
import { renderSupportIcon } from '.';
import { useTranslation } from 'react-i18next';
import { translationKeys } from '../../i18n/en';
import { HTMLParser, SvgIcon, Tooltip } from '@gik/ui';

export interface ITimelineProps {
  data: IHistoryEntry[];
}

export const Timeline = ({ data }: ITimelineProps) => {
  const bem = useBemCN('timeline');
  const { t } = useTranslation();

  const dateDisplayFormat = 'MMM Do';

  function renderLabel(item: IHistoryEntry) {
    switch (item.type) {
      case 'purchase':
        return (
          <span>
            {item.productCheckoutType === 'gik-premium' ? (
              t(translationKeys.premiumPurchased)
            ) : item.calendarEntry ? (
              <HTMLParser
                rawHtml={t(translationKeys.eventGiftedCalendar, {
                  price: item.price,
                  name: item.productName,
                  typeName: item.calendarEntry.calendarEventTypeName,
                  date: moment.utc(item.calendarEntry.startsAt).format(dateDisplayFormat),
                })}
              />
            ) : (
              <HTMLParser
                rawHtml={t(translationKeys.eventGifted, {
                  price: item.price,
                  name: item.productName,
                })}
              />
            )}
            {item.isPendingFulfillment && (
              <div {...bem('pending-fulfillment')}>
                &nbsp;-&nbsp;<strong>{t(translationKeys.pendingFulfillment)}</strong>&nbsp;
                <Tooltip text={t(translationKeys.tangoCardFulfillmentInfo)}>
                  <div style={{ display: 'inline-block' }}>
                    <SvgIcon name="InformationCircle" size="sm" />
                  </div>
                </Tooltip>{' '}
              </div>
            )}
          </span>
        );
      case 'follow':
        return (
          <span>
            <HTMLParser rawHtml={t(translationKeys.eventFollow)} />
          </span>
        );
      case 'unfollow':
        return (
          <span>
            <HTMLParser rawHtml={t(translationKeys.eventUnfollow)} />
          </span>
        );
      case 'claim':
        return (
          <span>
            <HTMLParser
              rawHtml={t(translationKeys.eventClaim, {
                name: item.calendarEntry?.calendarEventTypeName,
                date: moment.utc(item.calendarEntry?.startsAt).format(dateDisplayFormat),
              })}
            />
          </span>
        );
      case 'unclaim':
        return (
          <span>
            <HTMLParser
              rawHtml={t(translationKeys.eventUnclaim, {
                name: item.calendarEntry?.calendarEventTypeName,
                date: moment.utc(item.calendarEntry?.startsAt).format(dateDisplayFormat),
              })}
            />
          </span>
        );
      // TODO: enable this when backend support is added
      // case 'donation':
      //   return (
      //     <span>
      //       Donation
      //     </span>
      //   );
      default:
        throw new Error(`unknown support type "${item.type}"`);
    }
  }

  return (
    <div {...bem()}>
      <div {...bem('line')}></div>
      {data?.map((item, index) => {
        return (
          <div key={`${index}-${item.createdDateTime}`} {...bem('item')}>
            <div {...bem('icon')}>{renderSupportIcon(item)}</div>
            <div {...bem('item-content')}>
              <span {...bem('item-title')}>{renderLabel(item)}</span>
              <span {...bem('item-date')}>{moment.utc(item.createdDateTime).fromNow()}</span>
            </div>
          </div>
        );
      })}
    </div>
  );
};
